import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, CheckCircle2, ExternalLink, AlertTriangle } from "lucide-react";
import { ujmsV2StartTask, ujmsV2FinishTask } from "../../services/ujmsApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Field, inputCls, btnPrimary, btnGhost, cls } from "../ui";

const OPEN = ["PENDING", "ASSIGNED", "ESCALATED"];

/** Start = claim the task and go do the work; Finish = close it (system tasks need an override reason). */
export function useTaskWork(onChanged) {
  const nav = useNavigate();
  const [finishing, setFinishing] = useState(null);
  const start = async (t) => {
    try {
      const r = await ujmsV2StartTask(t.id);
      const w = r.data.work || t.work;
      toast.success(w?.route ? `Started — opening: ${w.label}` : "Task started");
      onChanged?.(r.data);
      if (w?.route) nav(w.route);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start task"); }
  };
  const openWork = (t) => { if (t.work?.route) nav(t.work.route); };
  return { start, openWork, finish: (t) => setFinishing(t), finishing, closeFinish: () => setFinishing(null) };
}

export function WorkHint({ t, className }) {
  if (!t?.work) return null;
  return (
    <div className={cls("text-[10.5px] text-slate-500", className)} data-testid={`task-work-hint-${t.id}`}>
      {t.work.auto ? <>Completes itself when <b>{t.work.completes_when}</b>.</> : t.work.route ? <>Finish it here when the work is done.</> : <>Manual task — finish when done.</>}
    </div>
  );
}

export function TaskActions({ t, work, compact, onChanged }) {
  const tw = work;
  if (t.status === "COMPLETED") return <span className="text-[10.5px] text-emerald-700 font-semibold" data-testid={`task-done-${t.id}`}>Completed</span>;
  const b = compact ? "text-[10.5px] py-0 px-1.5" : "text-[11px] py-0.5";
  return (
    <span className="inline-flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
      {OPEN.includes(t.status) && <button className={cls(btnPrimary, b)} onClick={() => tw.start(t)} data-testid={`task-start-${t.id}`}><Play className="w-3 h-3" /> Start</button>}
      {t.status === "IN_PROGRESS" && t.work?.route && <button className={cls(btnGhost, b)} onClick={() => tw.openWork(t)} data-testid={`task-open-work-${t.id}`}><ExternalLink className="w-3 h-3" /> {t.work.label || "Open work"}</button>}
      <button className={cls(t.work?.auto ? btnGhost : btnPrimary, b, t.work?.auto && "!border-amber-300 !text-amber-800")} onClick={() => tw.finish(t)} data-testid={`task-finish-${t.id}`}>
        <CheckCircle2 className="w-3 h-3" /> {t.work?.auto ? "Finish anyway" : "Finish"}
      </button>
    </span>
  );
}

export function FinishTaskDialog({ task, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const auto = !!task?.work?.auto;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsV2FinishTask(task.id, { note, override_reason: reason });
      toast.success(auto ? "Task finished by override — reason recorded" : "Task finished");
      setNote(""); setReason(""); onClose(); onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not finish task"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="task-finish-dialog">
        <DialogHeader><DialogTitle>{auto ? "Finish task anyway" : "Finish task"}</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-700 -mt-1" data-testid="task-finish-title">{task?.caseNumber ? `${task.caseNumber} — ` : ""}{task?.title}</div>
        {auto && (
          <div className="flex gap-2 border border-amber-300 bg-amber-50 text-amber-900 text-[11.5px] px-2 py-1.5 rounded-sm" data-testid="task-finish-override-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
            <div>This task completes itself when <b>{task.work.completes_when}</b>. Finishing it by hand is an override: it is recorded in the task history and the audit log with your reason.</div>
          </div>
        )}
        {auto && <Field label="Override reason" required><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being closed without the action?" data-testid="task-finish-reason" /></Field>}
        <Field label={auto ? "Completion note (optional)" : "Completion note"}><textarea className={`${inputCls} h-16`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was done" data-testid="task-finish-note" /></Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose} data-testid="task-finish-cancel">Cancel</button>
          <button className={btnPrimary} disabled={busy || (auto && !reason.trim())} onClick={submit} data-testid="task-finish-submit">{busy ? "Finishing…" : auto ? "Finish anyway" : "Finish task"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
